import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef, GridRowId, GridRowParams, useGridApiRef } from '@mui/x-data-grid-pro';
import Controls from '../Controls';
import { EditDialog, EditorModus, } from '../../components/EditDialog/EditDialog';
import { Box, CheckboxProps, useTheme } from '@mui/material';
import { PlainSearchOptions, StaticSearchOptions, usePlainSearch, } from '../../../utils/utilHooks';
import { EditPanelDataGrid, EditPanelDataGridProps } from '../../components/EditPanelDataGrid/EditPanelDataGrid';
import { TEntity } from '../../../../models/entity';
import { MultiSelectCtxMenu } from './MultiSelectCtxMenu';
import { ToolbarCheckbox } from '../../components/EditPanelDataGrid/EditPanelDataGridToolbar';
import makeStyles from '@mui/styles/makeStyles';
import { useEditPanelContext } from '../../components/AdminDataGrid/EditPanelContext';
import { ButtonTypography, GreyOutlinedButton } from '../../components/Buttons/buttons';
import { useRememberSelectionModel } from '../../components/EditPanelDataGrid/editPanelDataGridHelper';
import clsx from 'clsx';

export type ModalMultiSelectProps<T extends TEntity> = {
    value: T[];
    columns: GridColDef[];
    title?: string;
    name: string;
    dialogTitle?: string;
    testId?: string;
    required?: boolean;
    isRowSelectable?: (params: GridRowParams) => boolean;
    onChange(value: T[]): void;
    isOpen: boolean;
    className?: string;
    disableMultipleSelection?: boolean;
    baseCheckbox?: React.ForwardRefExoticComponent<Omit<CheckboxProps, 'ref'> & React.RefAttributes<HTMLButtonElement>>;
    close(): void;
    hiddenInput?: boolean;
    validateFunction?: (fieldValue: any, key: string) => boolean | undefined;
    pagination?: boolean;
    isOptionEqualToValue?: (option: T, selected: T) => boolean;
    disableColumnVisibilitySaving?: boolean;
    disableColumnSettings?: boolean;
    gridId: string;
} & Pick<EditPanelDataGridProps<T>, 'isRowSelectable' | 'getRowClassName'>;

export function getSelectionModel<T extends TEntity>(
    selected: T[],
    selectionModel?: GridRowId[]
) {
    return (
        selectionModel?.some(i => i != null) ? selectionModel :
            selected.filter((el) => el).map((el) => el.id as GridRowId)
    );
}

export const dataGridSelectStyle = makeStyles((theme) => ({
    datagrid: {
        '& .MuiTablePagination-displayedRows ': {
            'display': 'none'
        },
        '& .MuiTablePagination-actions ': {
            'color': theme.palette.primary.main
        },
    },
    buttonSearch: {
        height: '100%',
        display: 'flex',
        'padding-top': '12px',
        'padding-bottom': '8px'
    },
}));

export function ModalMultiSelect<T extends TEntity>(
    props: ModalMultiSelectProps<T> &
        (PlainSearchOptions<T> | StaticSearchOptions<T>)
) {
    const {
        value: selected,
        columns,
        options: inputOptions,
        isOpen,
        dialogTitle = '',
        plainSearch = true,
        title = 'Redigere',
        close,
        onChange,
        disableColumnVisibilitySaving = false,
        isOptionEqualToValue,
        className = ''
    } = props;
    const apiRef = useGridApiRef();
    const isInitializedValueRef = React.useRef(false);
    const theme = useTheme();
    const [currentContextItem, setCurrentCtxItem] = useState<T>();

    const [kunValgte, setKunValgte] = useState(false);

    const isSelectedEqualToItem = useCallback((selected: T, item: T) => selected.id === item.id, []);
    const {
        syncRememberedSelections,
        rememberedSelectedValue,
        setRememberedSelectedValue,
        setIsSelected,
        returnSelectionRememberModel
    } =
        useRememberSelectionModel({ isSelectedEqualToItem });

    const [pristine, setPristine] = useState(false);
    const [options, setOptions] = useState<T[]>(inputOptions ?? []);
    const { getOptions } = props as PlainSearchOptions<T>;

    async function getSearchOptions(searchString: string) {
        if (getOptions) return await getOptions(searchString);
        else return new Promise<T[]>((res) => res(inputOptions ?? ([] as T[])));
    }

    const { loading, searchString, setSearchString } =
        usePlainSearch<T>({
            minimumSearchLength: 0,
            setOptions,
            getOptions: getSearchOptions,
            open: isOpen,
        });
    const saveClose = () => {
        const values = Array.from(rememberedSelectedValue.values()) as T[];
        onChange(values);
        close();
    };

    useEffect(() => {
        if (selected && isOpen && !loading && options.length > 0 && !isInitializedValueRef.current) {
            let equalValue = selected;
            if (isOptionEqualToValue) {
                equalValue = options.filter((option) =>
                    selected.some((value) => isOptionEqualToValue(option, value))
                );
            }
            setRememberedSelectedValue(prev => {
                const prevSet = new Set(prev);
                equalValue.forEach((selected) => {
                    if (Array.from(prev.values()).findIndex(i => i.id === selected.id) === -1) {
                        prevSet.add(selected);
                    }
                });
                return prevSet;
            });
            isInitializedValueRef.current = true;
        }
    }, [isOptionEqualToValue, isOpen, options, selected, loading, setRememberedSelectedValue]);
    const handleSelectionModelChange = useCallback(
        (selections: T[]) => {
            setPristine(true);
            if (props.disableMultipleSelection) {
                setRememberedSelectedValue(prev => {
                    const prevValues = Array.from(prev.values());
                    const item = selections.filter(i => !prevValues.some(j => j.id === i.id))[0];
                    const newSet = new Set<T>();
                    if (item) {
                        setIsSelected(true);
                        newSet.add(item);
                    }
                    return newSet;

                });
                return;
            }
            setRememberedSelectedValue((prevState) => {
                const prev = new Set(prevState);
                prev.forEach((i: T) => {
                    if (
                        (options ?? []).some((item) => i.id === item.id) &&
                        !selections.some((selected) => selected.id === i.id)
                    ) {
                        prev.delete(i);
                    }
                });
                selections.forEach((selected) => {
                    if (Array.from(prev.values()).findIndex(i => i.id === selected.id) === -1) {
                        prev.add(selected);
                    }
                });
                setIsSelected(prev.size > 0);
                return prev;
            });
        },
        [setRememberedSelectedValue, setIsSelected, options]
    );

    function handleKunValgteChanged(e: any, checked: boolean) {
        setKunValgte(checked);
    }

    const handleSelect = useCallback(() => {
        if (currentContextItem)
            setRememberedSelectedValue(prevState => {
                const prev = new Set(prevState);
                prev.add(currentContextItem);
                return prev;
            });
        setPristine(true);
    }, [currentContextItem, setRememberedSelectedValue]);

    const handleUnSelect = useCallback(() => {
        if (currentContextItem)
            setRememberedSelectedValue(prevState => {
                const prev = new Set(prevState);
                prev.delete(currentContextItem);
                return prev;
            });
        setPristine(true);
    }, [currentContextItem, setRememberedSelectedValue]);

    function handleSearch() {
        setSearchString('');
    }

    function onInputChange(evt: any) {
        setSearchString(evt?.target?.value ?? '');
    }

    const style = dataGridSelectStyle();
    const { validateField } = useEditPanelContext<TEntity>();
    const handleLoseFocus = useCallback(
        () => {
            if ((!validateField && !props.validateFunction) || !props.required) return;
            if (validateField) validateField(props.value, props.name);
            if (props.validateFunction) props.validateFunction(props.value, props.name);
        },
        [validateField, props]
    );
    const currentRows = useMemo(() => {
        if (kunValgte)
            return Array.from(rememberedSelectedValue.values())
                .filter(i => options.some(j => j['id'] === i['id']))
                .map(i => options.find(j => j['id'] === i['id']) ?? i);
        return options;
    }, [kunValgte, options, rememberedSelectedValue]);

    useEffect(() => {
        syncRememberedSelections(currentRows ?? []);
    }, [currentRows, syncRememberedSelections]);
    const selectionModel = useMemo(
        () => returnSelectionRememberModel()
        , [returnSelectionRememberModel]);
    return (
        <EditDialog
            open={props.isOpen}
            editorModus={EditorModus.Opprett}
            canSave={pristine && !loading}
            onSave={saveClose}
            testId={props.testId + '-select'}
            onCancel={() => {
                props.close();
                handleLoseFocus();
            }}
            maxWidth={'md'}
            width={856}
            height={726}
            fullWidth={false}
            title={dialogTitle ? dialogTitle : title}
            lagreBtnTitle={'Ok'}
        >
            <Box display='flex' flex={'1'} flexDirection='column'>
                <Box display='flex' flexDirection='row' alignItems='center' gap={2} hidden={!plainSearch}>
                    <Controls.TextField
                        label={'Søk'}
                        name={'sok'}
                        value={searchString ?? ''}
                        onChange={onInputChange}
                        inputProps={{ 'test-id': props.testId + '-sok-inpt' }}
                    />
                    <div className={style.buttonSearch}>
                        <GreyOutlinedButton disabled={!searchString.length}
                            onClick={handleSearch}><ButtonTypography>Tøm</ButtonTypography></GreyOutlinedButton>
                    </div>
                </Box>

                <EditPanelDataGrid
                    apiRef={apiRef}
                    gridId={
                        !props.disableColumnSettings ? `modal_${props.gridId}` : undefined
                    }
                    getRowClassName={props.getRowClassName}
                    isRowSelectable={props.isRowSelectable}
                    searchMode
                    disableColumnVisibilitySaving={disableColumnVisibilitySaving}
                    headerLabel={`${title}${options?.length !== 0 ? ' (' + options?.length + ')' : ''}`}
                    rows={currentRows}
                    height='inherit'
                    loading={loading}
                    hideFooterRowCount
                    columns={columns}
                    disableMultipleSelection={props.disableMultipleSelection}
                    autoHeight={false}
                    autoPageSize={true}
                    pagination={false}
                    className={clsx(style.datagrid, className)}
                    testId={props.testId + '-select'}
                    checkboxSelection={true}
                    getCurrentItemCtxMenu={setCurrentCtxItem}
                    selectionModel={selectionModel}
                    hideFooter
                    hideFooterSelectedRowCount
                    hideFooterPagination
                    setSelectedRows={handleSelectionModelChange}
                    disableSelectionOnClick={false}
                    density='compact'
                    contentContextMenu={
                        <MultiSelectCtxMenu
                            unSelect={handleUnSelect}
                            select={handleSelect}
                        />
                    }
                    components={{ BaseCheckbox: props.baseCheckbox }}
                    componentsProps={{
                        toolbar: {
                            showRefresh: false,
                            headerTextProps: {
                                color: theme.palette.primary.main,
                            },
                            buttons: (
                                <ToolbarCheckbox
                                    value={kunValgte}
                                    color='lightBlue'
                                    title='Vis valgte'
                                    name='kunValgte'
                                    onChange={handleKunValgteChanged}
                                    testId={'-visValgte-'}
                                />
                            ),
                        },
                    }}
                />
            </Box>
        </EditDialog>
    );
}
